define([
  'underscore',
  'es6!sumo/util',
  'es6!launcher/util/listbuilder/wysiwyg/constants',
], (_, Util, WysiwygConstants) => {
  function getComponent(components, id) {
    let componentToReturn = null;

    _.each(components, component => {
      if (component.id === id) {
        componentToReturn = component;
      }
    });

    return componentToReturn;
  }

  function getComponentIndex(components, id) {
    if (Array.isArray(components)) {
      for (let i = 0; i < components.length; i++) {
        const component = components[i];
        if (component.id === id) return i;
      }

      return -1;
    } else {
      let index = -1;
      components.forEach((component, idx) => {
        if (index > -1) return false;
        if (component.get('id') === id) {
          index = idx;
          return false;
        }
      });
      return index;
    }
  }

  function removeComponent(components, id) {
    let componentToReturn = null;

    _.each(components, (component, index)  => {
      // If we remove a component, one of the components will come back
      // undefined.  There is no way to break out of the each loop.
      if (!component) return;
      if (component.id === id) {
        componentToReturn = components.splice(index, 1)[0];
      }
    });

    return componentToReturn;
  }

  function addIdsToComponents(components) {
    _.each(components, component => {
      if (!component.id) component.id = Util.generateId64();

      if (WysiwygConstants.OPTION_COMPONENTS.indexOf(component.type) >= 0) {
        if (!component.options || !component.options.length) return;

        // Add ids to each option if doesn't have it.
        _.each(component.options, option => {
          if (!option.id) option.id = Util.generateId64();
        });
      }

      // Add ids to child components
      if (!_.isEmpty(component.childComponents)) {
        addIdsToComponents(component.childComponents);
      }
    });
  }

  // Removes ids from components and it's options is it has them.
  function removeIdsFromComponents(components) {
    _.each(components, component => {
      component.id = undefined;

      if (WysiwygConstants.OPTION_COMPONENTS.indexOf(component.type) >= 0) {
        if (!component.options || !component.options.length) return;

        // Add ids to each option if doesn't have it.
        _.each(component.options, option => {
          option.id = undefined;
        });
      }
    });
  }

  // Removes ids from components and it's options is it has them.
  function removeSrcFromComponents(components) {
    _.each(components, component => {
      component.src = undefined;
    });
  }

  function removeErrorsFromComponents(components) {
    _.each(components, component => {
      component.errors = undefined;
    });
  }

  function addBrowserPrefixesToStyles(style) {
    // Only ms should be lower case.
    // https://facebook.github.io/react/tips/inline-styles.html
    const VENDOR_PREFIXES = ['Webkit', 'Moz', 'ms', 'O'];

    _.each(VENDOR_PREFIXES, prefix => {
      style[`${prefix}BorderTopLeftRadius`] = style.borderTopLeftRadius;
      style[`${prefix}BorderTopRightRadius`] = style.borderTopRightRadius;
      style[`${prefix}BorderBottomLeftRadius`] = style.borderBottomLeftRadius;
      style[`${prefix}BorderBottomRightRadius`] = style.borderBottomRightRadius;
      style[`${prefix}BoxShadow`] = style.boxShadow;
    });
  }

  /**
   * Returns a flat array of all components that are descendants of the given `components`
   */
  function getDescendantComponents(components, allComponents = [], opts = {}) {
    if (!components || !components.count()) return allComponents;
    const { responsiveHiddenOn } = opts;

    components.filter(component => (
      !responsiveHiddenOn
      || !component.get('responsiveHiddenOn')
      || !component.getIn(['responsiveHiddenOn', responsiveHiddenOn])
    )).forEach((component) => {
      if (!component.isEmpty()) {
        allComponents.push(component);

        if (component.has('childComponents')) {
          getDescendantComponents(component.get('childComponents'), allComponents, opts);
        }
      }
    });

    return allComponents;
  }

  /**
   * POJO version of getDescendantComponents
   */
  // FIXME: stick with pojos or immutable, not both
  function getDescendantComponentsJSON(components, allComponents = []) {
    components.forEach((component) => {
      if (!_.isEmpty(component)) {
        allComponents.push(component);

        if ('childComponents' in component) {
          getDescendantComponentsJSON(component.childComponents, allComponents);
        }
      }
    });

    return allComponents;
  }

  /**
   * Returns the component with the given id by searching the given components and their descendants
   */
  function getComponentWithId(components, id) {
    return _.find(getDescendantComponents(components), component => component.get('id') === id);
  }

  /**
   * Returns a key path to the given component or component id
   */
  // NOTE: this could really just be a convenient util for immutable by taking out the explicit
  // 'childComponents' stuff
  function getKeyPathForComponent(components, searchComponent, results = { found: false, indexPath: [] }) {

    if (!searchComponent) {
      return results.indexPath;
    }

    // make sure we're working with an id
    let searchComponentId = searchComponent;

    if (typeof searchComponentId !== 'string') {
      searchComponentId = searchComponent.get('id');
    }

    components.forEach((component, index) => {
      if (results.found === false) {
        results.indexPath.push(index);
        results.indexPath.push('childComponents');

        if (component.get('id') === searchComponentId) {
          // slice off one 'childComponents' key path
          results.indexPath.pop();
          results.found = true;

          return false; // break - thanks immutable!
        } else if (component.has('childComponents')) {
          getKeyPathForComponent(component.get('childComponents'), searchComponentId, results);
        }

        // didn't find it down this line, pop the index and childComponent keys
        if (results.found === false) {
          results.indexPath.splice(-2);
        }
      }

      return true;
    });

    return results.indexPath;
  }

  /**
   * Returns the lineage of the given component (or component id), an array of components from
   * highest ancestor, to the given component, including the given component
   */
  function getComponentLineage(components, component) {
    const keyPathForComponent = getKeyPathForComponent(components, component);
    const lineage = [];
    let value = components;

    if (keyPathForComponent) {
      keyPathForComponent.forEach((keyPath) => {
        value = value.get(keyPath);

        if (keyPath !== 'childComponents') {
          lineage.push(value);
        }
      });
    }

    return lineage;
  }

  /**
   * Remove component with the given id from the given immutable components or their descendants
   */
  function removeComponentWithId(components, id) {
    const keyPathForComponent = getKeyPathForComponent(components, id);

    return (keyPathForComponent ? components.deleteIn(keyPathForComponent) : components);
  }

  /**
   * Returns the nearest ancestor of the given `component` or id, that matches the given `predicate`
   */
  function getNearestAncestorMatchingPredicate(rootComponents, id, predicate) {
    const componentLineage = getComponentLineage(rootComponents, id);
    let nearestAncestor;

    if (componentLineage) {
      let component;
      while ((component = componentLineage.pop())) {
        if (predicate(component)) {
          nearestAncestor = component;
          break;
        }
      }
    }

    return nearestAncestor;
  }

  /**
   * Returns true if the given `targetComponent` is a valid destination for the given `component`
   */
  function isValidParentForComponent(targetComponent, component, responsiveHiddenOn) {
    const type = component.get('type');
    const targetType = targetComponent.get('type');
    let isValidDropTarget = false;

    if (targetComponent.getIn(['responsiveHiddenOn', responsiveHiddenOn]))
      return isValidDropTarget;

    if (type === 'background') {
      isValidDropTarget = (targetType === 'gridColumn');
    } else {
      isValidDropTarget = (targetType === 'scaledStage');
    }

    return isValidDropTarget;
  }

  /**
   * Returns the nearest ancestor of `topComponent` that is a valid parent of `draggingComponent`
   */
  function getNearestValidParentComponent(rootComponents, topComponent, childComponent) {
    const c = getNearestAncestorMatchingPredicate(
      rootComponents,
      topComponent,
      component => isValidParentForComponent(component, childComponent),
    );

    return c;
  }

  /**
   * Returns the siblings of the given `siblingComponent`, excluding the given sibling
   * returns an empty array if no siblings are found
   * `siblingComponent` can also be a component id
   */
  function getComponentSiblings(rootComponents, siblingComponent) {
    const componentLineage = getComponentLineage(rootComponents, siblingComponent);
    const componentLineageLength = componentLineage.length;
    let siblingComponentId = siblingComponent;
    let componentSiblings = [];

    if (typeof siblingComponent !== 'string') {
      siblingComponentId = siblingComponent.get('id');
    }

    if (componentLineageLength > 2) {
      const componentParent = (componentLineage[componentLineageLength - 2]);
      componentSiblings = componentParent
        .get('childComponents')
        .filter(c => c.get('id') !== siblingComponentId);
    }

    return componentSiblings;
  }

  return {
    getComponent,
    getComponentIndex,
    removeComponent,
    addIdsToComponents,
    removeIdsFromComponents,
    removeSrcFromComponents,
    removeErrorsFromComponents,
    addBrowserPrefixesToStyles,
    getDescendantComponents,
    getDescendantComponentsJSON,
    getComponentWithId,
    getKeyPathForComponent,
    getComponentLineage,
    removeComponentWithId,
    getNearestAncestorMatchingPredicate,
    isValidParentForComponent,
    getNearestValidParentComponent,
    getComponentSiblings,
  };
});
